import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            options: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              type?: 'standard' | 'icon';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              width?: number;
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              locale?: string;
            }
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface GoogleSignInButtonProps {
  onSuccess?: () => void;
  width?: number;
  theme?: 'outline' | 'filled_blue' | 'filled_black';
}

export default function GoogleSignInButton({ onSuccess, width = 280, theme = 'filled_black' }: GoogleSignInButtonProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const { login } = useAuth();

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn('VITE_GOOGLE_CLIENT_ID not set — Google Sign-In unavailable.');
      return;
    }

    const initButton = () => {
      if (!window.google?.accounts?.id || !buttonRef.current) return;

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          if (response.credential) {
            await login(response.credential);
            onSuccess?.();
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        theme,
        size: 'large',
        type: 'standard',
        shape: 'pill',
        width,
        text: 'continue_with',
      });
    };

    // Google GSI may or may not be loaded yet
    if (window.google?.accounts?.id) {
      initButton();
    } else {
      // Poll until the GSI script loads
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval);
          initButton();
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [login, onSuccess, width, theme]);

  return (
    <div
      ref={buttonRef}
      style={{ display: 'flex', justifyContent: 'center', minHeight: '44px' }}
    />
  );
}
